// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Conduit — one run, whether that is you against two bots or six people in a room.
//
// THE WIRE CARRIES RESULTS, NEVER STATE. Every peer derives the identical deal from the seed the
// host froze into the round start, so there is nothing to keep in sync and nothing that can desync:
// no snapshots, no input relay, no host authority over anything that matters. Host transfer is a
// display concern, because nobody was holding anything.
//
// FOUR THINGS HERE ARE NOT OBVIOUS AND EACH FIXES A SPECIFIC WAY THIS SHAPE BREAKS:
//
//  1. EVERY PACKET CARRIES THE MATCH ID. rematch.ts's round number counts MATCHES; conduit's own
//     rounds count 1..7 inside one. Without a match id a match-1 packet with r=7 is indistinguishable
//     from a match-2 packet with r=7, so a late `fin` from the previous match paints a stale plate
//     into the live summary and nothing can detect it.
//
//  2. AWAY IS KEYED OFF A HEARTBEAT, NOT OFF THE LEDGER. A healthy seat is silent between round
//     barriers for as long as it takes to think, which is far longer than any sensible away timeout.
//     Keying away off ledger silence would mark every live player away, every round.
//
//  3. `fin` RETRANSMITS UNTIL ACKED. The ledger self-heals because each one is a superset of the
//     last, but the finish packet is a single shot — and a backgrounded tab drops WebRTC messages.
//     One lost `fin` would leave that player's summary card permanently blank, which is a dead end on
//     the one screen that must never have one.
//
//  4. THE CLAIM TIER IS RECOMPUTED, NEVER PATCHED, AND NEVER TRAVELS. Who was first to a vent is a
//     function of the whole table's ledgers, so it is derived locally from whatever has arrived and
//     re-derived from scratch whenever anything new lands. A peer missing one ledger is briefly
//     optimistic and then correct; it is never wrong in a way that sticks.
//
// AND THE BUILD-MISMATCH CHECK ONLY COMPARES WHAT THE PLATE CAN PROVE. Scrap count and claim tier are
// not derivable from 49 bytes of plate, so comparing a full total would fire on honest peers roughly
// whenever anyone scrapped. It compares the plate-derived subtotal and reports a divergence as a
// stale build, never as cheating.

import { Game, scorePlate, type ScoreDetail } from './game';
import { decodePlate, encodePlate, VENT_COUNT } from './plate';
import { PER_ROUND, type Mode } from './modes';
import { SKILLS, playOut } from './bots';
import { SEAT_GLYPH, SEAT_INK } from './palette';

export interface SeatState {
  id: string;
  name: string;
  you: boolean;
  bot: boolean;
  glyph: string;
  ink: string;
  /** Round in which this seat first sealed each vent; -1 for never. The 12-byte ledger. */
  led: number[];
  marks: number;
  round: number;
  /** The seat's own claimed running score, for the live rail. */
  sc: number;
  /** Finished plate, once it arrives. */
  plate: string | null;
  /** The seat's own plate-derived subtotal, for the build-mismatch check. */
  claimedSolo: number | null;
  scraps: number;
  ended: boolean;
  heard: number;
  mismatch: boolean;
}

export interface SessionDeps {
  send: (m: unknown) => void;
  onChange: () => void;
  onEnd: (s: Summary) => void;
}

/** A seat is presumed away after this much heartbeat silence. Never rely on onPeerLeave alone. */
export const AWAY_MS = 20000;
export const BEAT_MS = 3000;
/** How long the summary waits for stragglers' plates before showing what it has. */
export const FINISH_GATE_MS = 12000;

type Wire =
  | { t: 'hb'; m: number; seat: number; r: number; marks: number; sc: number }
  | { t: 'led'; m: number; seat: number; r: number; led: number[]; sc: number; ended: 0 | 1 }
  | { t: 'fin'; m: number; seat: number; plate: string; led: number[]; solo: number; scraps: number; sc: number }
  | { t: 'fna'; m: number; seat: number };

export interface SeatResult {
  name: string;
  you: boolean;
  bot: boolean;
  glyph: string;
  ink: string;
  score: ScoreDetail | null;
  total: number;
  plate: string | null;
  /** Vents this seat was first to seal. */
  firsts: number;
  complete: boolean;
  away: boolean;
  mismatch: boolean;
}

export interface Summary {
  lines: SeatResult[];
  mode: Mode;
  /** Vents nobody at the table ever sealed — the "what everyone missed" line. */
  missed: number[];
  waiting: boolean;
}

export class Session {
  readonly game: Game;
  readonly mode: Mode;
  readonly seats: SeatState[] = [];
  readonly match: number;
  readonly solo: boolean;
  private deps: SessionDeps;
  private mySeat = 0;
  private bots: Array<{ seat: SeatState; game: Game }> = [];
  private finishedAt = 0;
  private ended = false;

  constructor(opts: {
    mode: Mode;
    seed: number;
    match: number;
    selfId: string;
    myName: string;
    peers: Array<{ id: string; name: string }>;
    deps: SessionDeps;
  }) {
    this.mode = opts.mode;
    this.game = new Game(opts.mode, opts.seed);
    this.match = opts.match;
    this.deps = opts.deps;
    this.solo = opts.peers.length <= 1;

    const roster = opts.peers.length > 0 ? opts.peers : [{ id: opts.selfId, name: opts.myName }];
    roster.forEach((p, i) => {
      this.seats.push(blankSeat(p.id, p.name, p.id === opts.selfId, false, i));
      if (p.id === opts.selfId) this.mySeat = i;
    });

    // SOLO ONLY. A bot that reacted to the local player would make different moves on every device,
    // so two peers would show each other different "same" opponents. In a room every rival is real.
    if (this.solo) {
      for (const [k, id] of (['steady', 'sharp'] as const).entries()) {
        const seat = blankSeat(`bot:${id}`, SKILLS[id].name, false, true, this.seats.length + k);
        this.seats.push(seat);
        const g = playOut(new Game(opts.mode, opts.seed), SKILLS[id]);
        const sc = g.score();
        seat.led = [...g.sealedIn];
        seat.plate = encodePlate(g.plate);
        seat.marks = g.log.length;
        seat.round = opts.mode.rounds;
        seat.sc = sc.total;
        seat.scraps = g.log.filter((e) => e.scrapped).length;
        seat.ended = true;
        this.bots.push({ seat, game: g });
      }
    }
  }

  /** Par: what the steady bot managed on this exact seed. Free — it is already sitting there. */
  par(): number | null {
    const b = this.bots[0];
    return b ? b.game.score().total : null;
  }

  get me(): SeatState {
    return this.seats[this.mySeat];
  }

  /**
   * Which tier this seat gets for a vent: 0 if it is among the earliest at the table, else 1.
   *
   * A pure function of the ledgers currently held, re-derived from scratch on every call. Arrival
   * order provably cannot affect it, because it only ever compares round numbers.
   */
  private tierFor(seatIdx: number): (vent: number) => 0 | 1 {
    return (v: number) => {
      const mine = this.seats[seatIdx]?.led[v] ?? -1;
      if (mine < 0) return 0;
      let best = mine;
      for (const s of this.seats) {
        const r = s.led[v] ?? -1;
        if (r >= 0 && r < best) best = r;
      }
      return mine <= best ? 0 : 1;
    };
  }

  liveScore(): number {
    return this.game.score(this.tierFor(this.mySeat)).total;
  }

  /** Called after every mark. Posts the ledger at a round barrier, and finishes the run. */
  afterMark(): void {
    const me = this.me;
    me.led = [...this.game.sealedIn];
    me.marks = this.game.log.length;
    me.round = this.game.round;
    me.sc = this.liveScore();
    me.scraps = this.game.log.filter((e) => e.scrapped).length;

    if (this.game.over) {
      this.finish();
      return;
    }
    if (this.game.roundComplete) this.sendLedger();
    this.deps.onChange();
  }

  private sendLedger(): void {
    if (this.solo) return;
    this.deps.send({
      t: 'led',
      m: this.match,
      seat: this.mySeat,
      r: this.game.round,
      led: [...this.game.sealedIn],
      sc: this.liveScore(),
      ended: this.game.over ? 1 : 0,
    } satisfies Wire);
  }

  private finTimer: ReturnType<typeof setInterval> | null = null;
  private finTries = 0;

  private finish(): void {
    if (this.ended) return;
    this.ended = true;
    const me = this.me;
    me.plate = encodePlate(this.game.plate);
    me.ended = true;
    me.claimedSolo = this.soloSubtotal(this.game.score(this.tierFor(this.mySeat)));
    this.finishedAt = Date.now();

    if (!this.solo) {
      const packet = (): Wire => ({
        t: 'fin',
        m: this.match,
        seat: this.mySeat,
        plate: me.plate!,
        led: [...this.game.sealedIn],
        solo: me.claimedSolo!,
        scraps: me.scraps,
        sc: me.sc,
      });
      this.deps.send(packet());
      // Retransmit until every live seat has acked. A single-shot finish packet is simply lost if
      // the tab was backgrounded at that instant, and that seat's card would be blank forever.
      this.finTimer = setInterval(() => {
        if (this.finTries++ > 15 || this.allAcked()) {
          if (this.finTimer) clearInterval(this.finTimer);
          this.finTimer = null;
          return;
        }
        this.deps.send(packet());
      }, 2000);
    }
    this.deps.onEnd(this.summary());
  }

  private acked = new Set<number>();
  private allAcked(): boolean {
    return this.liveSeats().every((s) => s.you || s.bot || this.acked.has(this.seats.indexOf(s)));
  }

  /** Re-send the finish packet to anyone who turns up late or is promoted while we sit on results. */
  reannounce(): void {
    if (this.solo || !this.ended) return;
    const me = this.me;
    if (!me.plate) return;
    this.deps.send({
      t: 'fin', m: this.match, seat: this.mySeat, plate: me.plate,
      led: [...this.game.sealedIn], solo: me.claimedSolo ?? 0, scraps: me.scraps, sc: me.sc,
    } satisfies Wire);
  }

  beat(): void {
    if (this.solo) return;
    this.deps.send({
      t: 'hb', m: this.match, seat: this.mySeat, r: this.game.round,
      marks: this.game.log.length, sc: this.me.sc,
    } satisfies Wire);
  }

  /**
   * Take a packet.
   *
   * Two guards before anything is applied: the match id must be ours, and the claimed seat index
   * must match the SENDER's index in the frozen roster. Without the second, a spectator (or a stale
   * packet crossing a host transfer) can overwrite a real seat's ledger and rewrite the whole
   * table's claim tiers.
   */
  receive(raw: unknown, from: string): void {
    const m = raw as Wire;
    if (!m || typeof m !== 'object' || typeof m.m !== 'number' || m.m !== this.match) return;
    const idx = this.seats.findIndex((s) => s.id === from);
    if (idx < 0 || idx !== m.seat) return;
    const seat = this.seats[idx];
    seat.heard = Date.now();

    if (m.t === 'hb') {
      seat.marks = num(m.marks, seat.marks);
      seat.round = num(m.r, seat.round);
      seat.sc = num(m.sc, seat.sc);
      this.deps.onChange();
      return;
    }
    if (m.t === 'fna') {
      this.acked.add(m.seat);
      return;
    }
    if (m.t === 'led') {
      if (!validLed(m.led)) return;
      seat.led = m.led.slice(0, VENT_COUNT);
      seat.sc = num(m.sc, seat.sc);
      seat.round = num(m.r, seat.round);
      if (m.ended === 1) seat.ended = true;
      this.deps.onChange();
      return;
    }
    if (m.t === 'fin') {
      if (!validLed(m.led)) return;
      const plate = decodePlate(m.plate);
      if (!plate) return;
      seat.plate = m.plate;
      seat.led = m.led.slice(0, VENT_COUNT);
      seat.scraps = num(m.scraps, 0);
      seat.sc = num(m.sc, seat.sc);
      seat.claimedSolo = num(m.solo, 0);
      seat.ended = true;
      // Score their plate with OUR scorer. A divergence means one of us is on a stale build; it is
      // never presented as anything else, and the locally recomputed number is what gets shown.
      const mine = this.soloSubtotal(scorePlate(plate, this.mode, { sealedIn: seat.led, scraps: seat.scraps }));
      seat.mismatch = mine !== seat.claimedSolo;
      this.deps.send({ t: 'fna', m: this.match, seat: this.mySeat } satisfies Wire);
      this.deps.onChange();
      if (this.ended) this.deps.onEnd(this.summary());
    }
  }

  /** The part of a score that a plate alone can prove. Claim and demand are table-derived. */
  private soloSubtotal(s: ScoreDetail): number {
    return s.net + s.run + s.offPath + s.ends + s.weep + s.scrap;
  }

  peerLeft(id: string): void {
    const s = this.seats.find((x) => x.id === id);
    if (s) s.heard = 0;
    this.deps.onChange();
  }

  private liveSeats(): SeatState[] {
    return this.seats.filter((s) => s.bot || s.you || Date.now() - s.heard < AWAY_MS);
  }

  /** True while the summary is still hoping for a plate from someone we can hear. */
  waitingForPlates(): boolean {
    if (Date.now() - this.finishedAt > FINISH_GATE_MS) return false;
    return this.liveSeats().some((s) => !s.you && !s.plate);
  }

  /**
   * The table.
   *
   * EVERY seat gets a row, always — including one that left at round 3 and never sent a plate. Its
   * row shows what it did post, and says so. A blank card, or a missing row, is the dead end this
   * screen exists to avoid.
   */
  summary(): Summary {
    const lines: SeatResult[] = [];
    for (let i = 0; i < this.seats.length; i++) {
      const s = this.seats[i];
      const tier = this.tierFor(i);
      let score: ScoreDetail | null = null;
      let total = s.sc;
      if (s.you) {
        score = this.game.score(tier);
        total = score.total;
      } else if (s.plate) {
        const p = decodePlate(s.plate);
        if (p) {
          score = scorePlate(p, this.mode, { sealedIn: s.led, scraps: s.scraps, claimTier: tier });
          total = score.total;
        }
      }
      let firsts = 0;
      for (let v = 0; v < VENT_COUNT; v++) if ((s.led[v] ?? -1) >= 0 && tier(v) === 0) firsts++;
      lines.push({
        name: s.name,
        you: s.you,
        bot: s.bot,
        glyph: s.glyph,
        ink: s.ink,
        score,
        total,
        plate: s.plate,
        firsts,
        complete: s.ended,
        away: !s.bot && !s.you && Date.now() - s.heard > AWAY_MS,
        mismatch: s.mismatch,
      });
    }
    lines.sort((a, b) => b.total - a.total);

    const missed: number[] = [];
    for (let v = 0; v < VENT_COUNT; v++) {
      if (!this.seats.some((s) => (s.led[v] ?? -1) >= 0)) missed.push(v);
    }
    return { lines, mode: this.mode, missed, waiting: this.waitingForPlates() };
  }

  /** Vent state for the plate furniture: 0 unsealed, 1 you sealed it, 2 a rival got there first. */
  ventStates(): Uint8Array {
    const out = new Uint8Array(VENT_COUNT);
    const tier = this.tierFor(this.mySeat);
    for (let v = 0; v < VENT_COUNT; v++) {
      const mine = this.game.sealedIn[v] >= 0;
      if (mine) out[v] = tier(v) === 0 ? 1 : 2;
      else out[v] = 0;
    }
    return out;
  }

  destroy(): void {
    if (this.finTimer) clearInterval(this.finTimer);
    this.finTimer = null;
    this.bots = [];
  }
}

function blankSeat(id: string, name: string, you: boolean, bot: boolean, i: number): SeatState {
  return {
    id, name, you, bot,
    glyph: SEAT_GLYPH[i % SEAT_GLYPH.length],
    ink: SEAT_INK[i % SEAT_INK.length],
    led: new Array(VENT_COUNT).fill(-1),
    marks: 0, round: 0, sc: 0, plate: null, claimedSolo: null, scraps: 0,
    ended: false, heard: Date.now(), mismatch: false,
  };
}

const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);

const validLed = (l: unknown): l is number[] =>
  Array.isArray(l) && l.length === VENT_COUNT && l.every((n) => typeof n === 'number' && n >= -1 && n < 64);

export { PER_ROUND };
